/**
 * Trunnion AI - Workflow API Endpoints
 */

import apiClient from './client';
import type {
  WorkflowDefinition,
  WorkflowListResponse,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  CloneWorkflowRequest,
} from '@/types';

const BASE = '/workflows';

export const workflowsApi = {
  list: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    classification?: string;
    isActive?: boolean;
  }): Promise<WorkflowListResponse> => {
    const { data } = await apiClient.get(BASE, { params });
    return data;
  },

  getById: async (id: string): Promise<WorkflowDefinition> => {
    const { data } = await apiClient.get(`${BASE}/${id}`);
    return data;
  },

  create: async (workflow: CreateWorkflowRequest): Promise<WorkflowDefinition> => {
    const { data } = await apiClient.post(BASE, workflow);
    return data;
  },

  update: async (
    id: string,
    workflow: UpdateWorkflowRequest,
  ): Promise<WorkflowDefinition> => {
    const { data } = await apiClient.put(`${BASE}/${id}`, workflow);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },

  execute: async (
    id: string,
    input?: Record<string, unknown>,
  ): Promise<{ executionId: string }> => {
    const { data } = await apiClient.post(`${BASE}/${id}/execute`, { input });
    return data;
  },

  clone: async (
    id: string,
    request: CloneWorkflowRequest,
  ): Promise<WorkflowDefinition> => {
    const { data } = await apiClient.post(`${BASE}/${id}/clone`, request);
    return data;
  },

  validate: async (
    id: string,
  ): Promise<{ valid: boolean; errors: string[]; warnings: string[] }> => {
    const { data } = await apiClient.post(`${BASE}/${id}/validate`);
    return data;
  },

  exportYaml: async (id: string): Promise<string> => {
    const { data } = await apiClient.get(`${BASE}/${id}/export`, {
      params: { format: 'yaml' },
      responseType: 'text',
    });
    return data;
  },

  exportJson: async (id: string): Promise<string> => {
    const { data } = await apiClient.get(`${BASE}/${id}/export`, {
      params: { format: 'json' },
    });
    return data;
  },
};
